<template>
  <div>
    <div v-if="showLoadingModal" class="loading-modal">
      <div class="loading-content">
        <div class="spinner"></div>
        <p>{{ $t('SEND_CONTRACT.FORM.LOADING_MESSAGE') }}</p>
      </div>
    </div>
    
    <div v-if="isDefaulterResponsibleWithSchool" class="p-4 text-sm text-red-400">
      {{ $t('SEND_CONTRACT.DEFAUTER_CONTACT_TOOLTIP') }}
    </div>
    
    <form v-if="!isDefaulterResponsibleWithSchool">
      <div v-if="!showPendingDocumentAlert">
        <div class="merge-contacts">
          <div class="multiselect-wrap--medium" v-if="schoolContracts.length === 0">
            <label class="multiselect__label">
              {{ $t('SEND_CONTRACT.SELECT.EMPTY_LABEL') }}
            </label>
          </div>
          <div
            class="multiselect-wrap--medium"
            v-if="schoolContracts.length > 0"
          >
            <label class="multiselect__label">
              {{ $t('SEND_CONTRACT.SELECT.TITLE') }}
            </label>
            <multiselect v-model="selectedEnrollment" :options="schoolContracts" label="student_name" track-by="id"
              :show-labels="false" :placeholder="$t('SEND_CONTRACT.SELECT.PLACEHOLDER')" :max-height="150"
              open-direction="top" />
            <div class="-mt-2">
              <span v-if="!$v.selectedEnrollment.hasFinishedDocument" class="error">
                {{ $t('SEND_CONTRACT.SELECT.HAS_FINISHED_DOCUMENT', {
                  year: selectedEnrollment &&
                    selectedEnrollment.school_year}) }}
              </span>
              <span v-else-if="!$v.selectedEnrollment.isValidSchoolYear" class="error">
                {{ $t('SEND_CONTRACT.SELECT.INVALID_SCHOOL_YEAR', {
                  year: selectedEnrollment && selectedEnrollment.school_year
                }) }}
              </span>
              <span v-else-if="!$v.selectedEnrollment.invalidEnrollmentDueDate" class="error">
                {{ $t('SEND_CONTRACT.SELECT.INVALID_ENROLLMENT_DUE_DATE') }}
              </span>
              <span v-else-if="!$v.selectedEnrollment.invalidContractDueDate" class="error">
                {{ $t('SEND_CONTRACT.SELECT.INVALID_CONTRACT_DUE_DATE') }}
              </span>
            </div>

          </div>

          <!-- Template Selection -->
          <div class="multiselect-wrap--medium" v-if="schoolContracts.length > 0">
            <label class="multiselect__label">
              {{ $t('SEND_CONTRACT.SELECT.TEMPLATE_TITLE') }}
            </label>
            <multiselect 
              v-model="selectedTemplate" 
              :options="templates" 
              label="name" 
              track-by="id"
              :show-labels="false" 
              :placeholder="$t('SEND_CONTRACT.SELECT.TEMPLATE_PLACEHOLDER')" 
              :max-height="150"
              :loading="isLoadingTemplates"
              open-direction="top" />
            <div class="-mt-2">
              <span v-if="!selectedTemplate" class="error">
                {{ $t('SEND_CONTRACT.SELECT.REQUIRED_TEMPLATE') }}
              </span>
              <span v-if="templates.length === 0 && !isLoadingTemplates" class="text-sm text-gray-500">
                Nenhum template disponível
              </span>
              <div v-if="templateWarningMessage" class="text-sm text-yellow-600 dark:text-yellow-400 mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                <svg class="w-4 h-4 inline-block mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                </svg>
                {{ templateWarningMessage }}
              </div>
            </div>
          </div>

          <div class="footer">
            <woot-button variant="clear" @click.prevent="onCancel">
              {{ $t('SEND_CONTRACT.FORM.CANCEL') }}
            </woot-button>
            <woot-button :is-loading="isRequestingContract" :disabled="$v.$invalid" @click.prevent="handleCheckDocuments">
              {{ $t('SEND_CONTRACT.FORM.SUBMIT') }}
            </woot-button>
          </div>
        </div>
      </div>
      <div v-if="showPendingDocumentAlert">
        <div class="text-sm text-red-400 mb-2">
          <span v-dompurify-html="$t('SEND_CONTRACT.SCHOOL_DOCUMENT_UNFINISHED')" />
        </div>

        <div class="flex-end gap-2">
          <woot-button :is-loading="isRequestingContract" :disabled="$v.$invalid" @click.prevent="onSubmit">
            {{ $t('SEND_CONTRACT.FORM.YES') }}
          </woot-button>

          <woot-button :is-loading="isRequestingContract" :disabled="$v.$invalid" @click.prevent="onCancel">
            {{ $t('SEND_CONTRACT.FORM.CANCEL') }}
          </woot-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import { required } from 'vuelidate/lib/validators';
import DigitalAcceptanceContractsAPI from '../../../api/digitalAcceptanceContracts';

export default {
  components: {},
  mixins: [alertMixin],
  props: {
    isRequestingContract: {
      type: Boolean,
      default: false,
    },
    contact: {
      type: Object,
      default: () => ({}),
    },
  },
  validations() {
    return {
      selectedEnrollment: {
        required,
        invalidEnrollmentDueDate: this.enrollmentDueDateValidation,
        invalidContractDueDate: this.contractDueDateValidation,
        hasFinishedDocument: this.hasCompletedSchoolDocumentForTheYear,
        isValidSchoolYear: this.isCurrentOrPastSchoolYear,
      },
      selectedTemplate: {
        required,
      },
    }
  },
  data() {
    return {
      selectedEnrollment: null,
      showPendingDocumentAlert: false,
      templates: [],
      selectedTemplate: null,
      isLoadingTemplates: false,
      templateWarningMessage: null,
      isCheckingTemplateUsage: false,
      showLoadingModal: false,
    };
  },
  mounted() {
    if (this.contact?.id) {
      this.$store.dispatch('schoolEnrollments/get', this.contact.id);
      this.$store.dispatch('contactLabels/get', this.contact.id);
    }
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      accountId: 'getCurrentAccountId',
      enrollments: 'schoolEnrollments/getSchoolEnrollments',
    }),
    contactLabels(){
      return this.$store.getters['contactLabels/getContactLabels'](this.contact.id);
    },
    isDefaulterResponsibleWithSchool(){
      return this.contactLabels.includes('inadimplente_com_a_escola');
    },
    schoolContracts() {
      return (this.enrollments || []).filter(enrollment => enrollment?.school_contract?.annuity);
    },
    schoolDocuments() {
      return this.selectedEnrollment?.school_documents;
    },
    hasPendingSchoolDocument() {
      return (this.schoolDocuments || []).filter(document => document.status === "pending").length > 0;
    },
    schoolAccount() {
      return this.getAccount(this.accountId).school_accounts?.find(school_account => school_account.school_document === this.selectedEnrollment?.enrollment_school_document);
    },
  },
  watch: {
    selectedEnrollment(newVal) {
      if (newVal) {
        this.loadTemplates();
      } else {
        this.templates = [];
        this.selectedTemplate = null;
        this.templateWarningMessage = null;
      }
    },
    selectedTemplate(newVal) {
      if (newVal && this.selectedEnrollment) {
        this.checkTemplateUsage();
      } else {
        this.templateWarningMessage = null;
      }
    },
  },
  methods: {
    async loadTemplates() {
      if (!this.selectedEnrollment) return;

      console.log('Loading templates for enrollment:', this.selectedEnrollment);
      this.isLoadingTemplates = true;
      
      try {
        // Extract company_id from enrollment data
        let companyId = null;
        
        // Try different possible locations for company_id
        if (this.selectedEnrollment.custom_attributes?.keducore?.company_id) {
          companyId = this.selectedEnrollment.custom_attributes.keducore.company_id;
        } else if (this.selectedEnrollment.school_contract?.custom_attributes?.keducore?.company_id) {
          companyId = this.selectedEnrollment.school_contract.custom_attributes.keducore.company_id;
        } else if (this.selectedEnrollment.company_id) {
          companyId = this.selectedEnrollment.company_id;
        } else if (this.selectedEnrollment.school_contract?.company_id) {
          companyId = this.selectedEnrollment.school_contract.company_id;
        }
        
        console.log('Extracted company_id:', companyId);
        
        if (companyId) {
          const response = await DigitalAcceptanceContractsAPI.forContact(this.contact.id).getTemplates(companyId);
          console.log('Templates response:', response.data);
          this.templates = response.data.templates;
          
          // Select default template if available
          const defaultTemplate = this.templates.find(template => template.isDefault);
          if (defaultTemplate) {
            this.selectedTemplate = defaultTemplate;
          }
        } else {
          console.error('No company_id found in enrollment:', this.selectedEnrollment);
          this.showAlert('Não foi possível identificar a empresa para carregar os templates');
        }
      } catch (error) {
        console.error('Error loading templates:', error);
        this.showAlert('Erro ao carregar templates de contrato');
      } finally {
        this.isLoadingTemplates = false;
      }
    },

    async checkTemplateUsage() {
      if (!this.selectedTemplate || !this.selectedEnrollment) return;

      this.isCheckingTemplateUsage = true;
      this.templateWarningMessage = null;

      try {
        const response = await DigitalAcceptanceContractsAPI.forContact(
          this.contact.id,
        ).checkTemplateUsage(
          this.selectedTemplate.id,
          this.selectedEnrollment.id,
        );

        if (response.data.template_used) {
          this.templateWarningMessage = response.data.message;
        }
      } catch (error) {
        // Don't show error to user, just log it
      } finally {
        this.isCheckingTemplateUsage = false;
      }
    },

    handleCheckDocuments() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      if (this.hasPendingSchoolDocument) {
        this.showPendingDocumentAlert = true;
      } else {
        this.onSubmit();
      }
    },

    async onSubmit() {
      try {
        this.showLoadingModal = true;
        this.$emit('update:isRequestingContract', true);
        
        const contractData = {
        digital_acceptance_contract: {
          school_enrollment_id: this.selectedEnrollment.id,
          template_id: this.selectedTemplate.id,
        },
      };

        const response = await DigitalAcceptanceContractsAPI.forContact(
          this.contact.id,
        ).createContract(contractData);

        this.showAlert('Contrato digital criado com sucesso!');
        this.$emit('success', response.data);
      } catch (error) {
        this.showAlert(
          'Erro ao criar contrato digital: ' + error.response?.data?.error,
        );
      } finally {
        this.showLoadingModal = false;
        this.$emit('update:isRequestingContract', false);
      }
    },

    onCancel() {
      this.selectedEnrollment = null;
      this.selectedTemplate = null;
      this.templates = [];
      this.showPendingDocumentAlert = false;
      this.templateWarningMessage = null;
      this.$emit('cancel');
    },
    getNextBusinessDay(date) {
      const nextDay = new Date(date);
      nextDay.setDate(date.getDate() + 1);

      while (nextDay.getDay() === 0 || nextDay.getDay() === 6) {
        nextDay.setDate(nextDay.getDate() + 1);
      }

      return nextDay;
    },
    enrollmentDueDateValidation(enrollment) {
      if (
        !enrollment ||
        !this.schoolAccount.generate_enrollment_bills_enable ||
        (this.schoolAccount.allow_enrollment_exemption && enrollment.enrollment_exempt)
      ) {
        return true;
      }

      if (enrollment?.tuition_due_date) {
        const enrollmentDueDate = new Date(
          enrollment.tuition_due_date + 'T00:00:00-03:00',
        );
        const currentDay = new Date();
        currentDay.setHours(0, 0, 0, 0);

        const firstBusinessDayAfterToday = this.getNextBusinessDay(currentDay);
        if (enrollmentDueDate >= firstBusinessDayAfterToday) {
          return true;
        }
      }
      return false;
    },
    contractDueDateValidation(enrollment) {
      if (!enrollment) return true;

      if (enrollment?.school_contract?.start_date_installment) {
        const enrollmentDueDate = new Date(
          enrollment.school_contract.start_date_installment + 'T00:00:00-03:00',
        );
        const currentDay = new Date();
        currentDay.setHours(0, 0, 0, 0);

        const firstBusinessDayAfterToday = this.getNextBusinessDay(currentDay);
        if (enrollmentDueDate >= firstBusinessDayAfterToday) {
          return true;
        }
      }
      return false;
    },
    hasCompletedSchoolDocumentForTheYear(enrollment) {
      if (!enrollment) return true;

      const completedDocuments = (enrollment.school_documents || []).filter(
        (document) =>
          document.status === 'completed' &&
          document.document_year === enrollment.school_year,
      );
      return completedDocuments.length === 0;
    },
    isCurrentOrPastSchoolYear(enrollment) {
      if (!enrollment) return true;
      const currentYear = new Date().getFullYear();
      const schoolYear = Number(enrollment.school_year);
      if (Number.isNaN(schoolYear)) return true;
      return schoolYear <= currentYear;
    },
  },
};
</script>

<style lang="scss" scoped>
.footer {
  margin-top: var(--space-medium);
  display: flex;
  justify-content: flex-end;
}
.error {
  @apply text-sm text-red-300 dark:text-red-500;
}
.loading-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.loading-content {
    background: white;
    padding: var(--space-large);
    border-radius: var(--border-radius-medium);
    text-align: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 200px;
}

.spinner {
    border: 4px solid var(--color-border);
    border-top: 4px solid var(--color-woot);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto var(--space-small);
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

</style>
